// Mutation wrappers with toast feedback. All destructive actions route through
// here so copy and error handling stay consistent.

import { enqueueSnackbar } from 'notistack';
import i18next from 'i18next';
import {
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useUpdateCampaignStatusMutation,
  useSendCampaignMutation,
  useTestSendCampaignMutation,
  useDuplicateCampaignMutation,
  useDeleteCampaignMutation,
  useRestoreCampaignMutation,
  useHardDeleteCampaignMutation,
  useExportAudienceMutation,
} from '../campaignsApi';
import { TOAST_ERROR, TOAST_SUCCESS } from '../../../utils/constants';

const runWithToast = async (fn, successKey, errorKey) => {
  try {
    const result = await fn().unwrap();
    enqueueSnackbar(i18next.t(successKey), { variant: TOAST_SUCCESS });
    return result;
  } catch (err) {
    enqueueSnackbar(i18next.t(errorKey), { variant: TOAST_ERROR });
    throw err;
  }
};

export const useCampaignsActions = () => {
  const [createTrigger] = useCreateCampaignMutation();
  const [updateTrigger] = useUpdateCampaignMutation();
  const [statusTrigger] = useUpdateCampaignStatusMutation();
  const [sendTrigger] = useSendCampaignMutation();
  const [testSendTrigger] = useTestSendCampaignMutation();
  const [duplicateTrigger] = useDuplicateCampaignMutation();
  const [deleteTrigger] = useDeleteCampaignMutation();
  const [restoreTrigger] = useRestoreCampaignMutation();
  const [hardDeleteTrigger] = useHardDeleteCampaignMutation();
  const [exportTrigger] = useExportAudienceMutation();

  return {
    createCampaign: (data) =>
      runWithToast(() => createTrigger(data), 'campaigns.created', 'campaigns.createFailed'),
    updateCampaign: (data) =>
      runWithToast(() => updateTrigger(data), 'campaigns.updated', 'campaigns.updateFailed'),
    updateStatus: (args) =>
      runWithToast(() => statusTrigger(args), 'campaigns.statusUpdated', 'campaigns.statusUpdateFailed'),
    sendCampaign: (args) =>
      runWithToast(() => sendTrigger(args), 'campaigns.sent', 'campaigns.sendFailed'),
    testSend: (args) =>
      runWithToast(() => testSendTrigger(args), 'campaigns.testSent', 'campaigns.testSendFailed'),
    duplicateCampaign: (id) =>
      runWithToast(() => duplicateTrigger(id), 'campaigns.duplicated', 'campaigns.duplicateFailed'),
    deleteCampaign: (id) =>
      runWithToast(() => deleteTrigger(id), 'campaigns.deleted', 'campaigns.deleteFailed'),
    restoreCampaign: (id) =>
      runWithToast(() => restoreTrigger(id), 'campaigns.restored', 'campaigns.restoreFailed'),
    hardDeleteCampaign: (id) =>
      runWithToast(() => hardDeleteTrigger(id), 'campaigns.hardDeleted', 'campaigns.hardDeleteFailed'),
    exportAudience: (args) =>
      runWithToast(() => exportTrigger(args), 'campaigns.exportSuccess', 'campaigns.exportFailed'),
  };
};
